import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AgentService } from '../../services/dossier.service';
import { AuthService } from '../../services/auth.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

@Component({
  selector: 'app-agent-chat',
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.css'],
})
export class AgentChatComponent implements OnInit {

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;

  /** Whether the chat panel is open */
  isOpen = false;

  /** Active agent tab */
  activeAgent: 'help' | 'ops' = 'help';

  /** Messages per agent */
  helpMessages: ChatMessage[] = [];
  opsMessages: ChatMessage[] = [];

  /** Session IDs */
  helpSessionId: string | null = null;
  opsSessionId: string | null = null;

  /** Input state */
  inputMessage = '';
  sending = false;

  /** Agent availability */
  helpAvailable = false;
  opsAvailable = false;

  /** Is user admin (for ops tab visibility) */
  get isAdmin(): boolean {
    return this.authService.hasRole('admin') || this.authService.isDeveloper;
  }

  constructor(
    private agentService: AgentService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.checkAvailability();
  }

  private checkAvailability(): void {
    this.agentService.helpStatus().subscribe({
      next: (res) => { this.helpAvailable = res.available; },
      error: () => { this.helpAvailable = false; },
    });
    if (this.isAdmin) {
      this.agentService.opsStatus().subscribe({
        next: (res) => { this.opsAvailable = res.available; },
        error: () => { this.opsAvailable = false; },
      });
    }
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  get currentMessages(): ChatMessage[] {
    return this.activeAgent === 'help' ? this.helpMessages : this.opsMessages;
  }

  sendMessage(): void {
    const msg = this.inputMessage.trim();
    if (!msg || this.sending) return;

    this.inputMessage = '';
    this.sending = true;

    const message: ChatMessage = { role: 'user', content: msg, timestamp: new Date() };

    if (this.activeAgent === 'help') {
      this.helpMessages.push(message);
      this.agentService.chatHelp(msg, this.helpSessionId || undefined).subscribe({
        next: (res) => {
          this.helpSessionId = res.session_id;
          this.helpMessages.push({ role: 'assistant', content: res.response, timestamp: new Date() });
          this.sending = false;
          this.scrollToBottom();
        },
        error: (err) => {
          this.helpMessages.push({
            role: 'assistant',
            content: err?.error?.detail || 'Sorry, something went wrong.',
            timestamp: new Date(),
          });
          this.sending = false;
          this.scrollToBottom();
        },
      });
    } else {
      this.opsMessages.push(message);
      this.agentService.chatOps(msg, this.opsSessionId || undefined).subscribe({
        next: (res) => {
          this.opsSessionId = res.session_id;
          this.opsMessages.push({ role: 'assistant', content: res.response, timestamp: new Date() });
          this.sending = false;
          this.scrollToBottom();
        },
        error: (err) => {
          this.opsMessages.push({
            role: 'assistant',
            content: err?.error?.detail || 'Sorry, something went wrong.',
            timestamp: new Date(),
          });
          this.sending = false;
          this.scrollToBottom();
        },
      });
    }

    this.scrollToBottom();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }
}
